@if (initiative || wizardForm) {
  <form [formGroup]="form" (ngSubmit)="onSubmit()" class="initiative-form" autocomplete="off">
    <nb-card [ngClass]="customNbCardClass">
      <nb-card-header>
        <div class="d-flex w-100">
          @if (isProposedLockedByWorkflow || isLocked) {
            @if (readOnly) {
              <a
                nbButton
                ghost
                status="primary"
                class="lock-button"
                nbTooltipPlacement="bottom"
              >
                <i class="fa-kit fa-light-lock-circle-question fa-xl"></i>
              </a>
            } @else {
              <a
                nbButton
                ghost
                status="primary"
                class="lock-button"
                [nbTooltip]="'Given the status some fields are not editable' | translate"
                nbTooltipPlacement="bottom"
              >
                <i class="fa-kit fa-light-lock-circle-question fa-xl"></i>
              </a>
            }
          }
          <div class="title">
            @if (site?.name) {
              <span class="title-site">{{ site?.name }}</span>
              <span class="title-separator"> &gt; </span>
            }
            <span class="title-entity">{{ initiative?.label }}</span>
          </div>
          <div style="position:absolute;right:20px;top:12px;display:flex;align-items:center;gap:20px;">
            @if (initiative) {
              <span (click)="openInvestment()" style="cursor:pointer;font-size:16px;font-weight:lighter;" [style.opacity]="initiative?.investment ? '1' : '0.6'">
                €({{ initiative?.investment ? 1 : 0 }})
              </span>
            }
            @if (canOpenActionSheet) {
              <span (click)="openActionSheet()" style="font-size:16px;cursor:pointer;display:flex;align-items:center;">
                <fa-icon icon="browsers"></fa-icon>
              </span>
            }
            <span class="close-btn" (click)="confirmClose()" style="font-size:16px;cursor:pointer;display:flex;align-items:center;">
              <i class="fa-light fa-x"></i>
            </span>
          </div>
        </div>
      </nb-card-header>

      <!-- FORM -->
      <nb-card-body>
        <!-- TABS -->
        @if (initiative && !addMode) {
          <div class="form-tabs-bar">
            <div class="form-tab" [class.active]="activeFormTab === 'description'" (click)="activeFormTab = 'description'">
              {{ "Description" | translate }}
            </div>
            <div class="form-tab" [class.active]="activeFormTab === 'documents'" (click)="activeFormTab = 'documents'">
              {{ "Documents" | translate }}
              @if (docCount > 0) {
                <span class="form-tab-count">({{ docCount }})</span>
              }
            </div>
          </div>
        }
        <div class="form-group" [style.display]="activeFormTab === 'description' ? '' : 'none'">
          <!-- LABEL -->
          <div class="form-field {{ form.controls['label'].value ? 'null' : 'highlighted' }}">
            <label for="label" class="{{ form.controls['label'].value ? 'null' : 'missing-field' }}">
              {{ 'Label' | translate }}
            </label>
            <input
              nbInput
              id="label"
              type="text"
              formControlName="label"
              fullWidth
              (blur)="autoSave('label')"
            >
          </div>
          <!-- STATUS -->
          <div class="form-field" [nbSpinner]="(statuses$ | async)?.loading">
            <label for="status">{{ 'Status' | translate }}</label>
            @if (form.get('status')?.disabled) {
              <div id="status" (click)="showLockedInitiativeDialog()">{{ getStatusDisplay() }}</div>
            } @else {
              <nb-select
                id="status"
                formControlName="status"
                fullWidth
              >
                @for (s of (statuses$ | async)?.data; track s) {
                  <nb-option
                    [value]="s.id"
                    [disabled]="(isStatusDisabled(s) | async)"
                    >
                    {{ s.name }}
                  </nb-option>
                }
              </nb-select>
            }
          </div>
          <!-- SAVINGS START AT -->
          <div class="form-field pb-2">
            <label for="perimeter">{{ 'Date of action' | translate }}</label>
            @if (form.get('savings_start_at')?.disabled) {
              <div id="savings_start_at_display" (click)="showLockedInitiativeDialog()">
                {{ formatMonthYear(form.get('savings_start_at')?.value) }}
              </div>
            } @else {
              <div class="datepicker-field">
                <input
                  nbInput
                  class="date-input"
                  id="savings_start_at"
                  readonly
                  [nbDatepicker]="savingsStartPicker"
                  [value]="formatMonthYear(form.get('savings_start_at')?.value)"
                  (click)="!savingsStartPicker.isShown && savingsStartPicker.show()"
                >
                <button type="button" class="datepicker-toggle" (click)="$event.preventDefault(); $event.stopPropagation(); savingsStartPicker.isShown ? savingsStartPicker.hide() : savingsStartPicker.show()">
                  <fa-icon [icon]="['fal', 'calendar']"></fa-icon>
                </button>
              </div>
              <nb-datepicker
                #savingsStartPicker
                startView="year"
                [date]="toDateValue(form.get('savings_start_at')?.value)"
                [visibleDate]="toDateValue(form.get('savings_start_at')?.value) || toDateValue(minStartDate)"
                [min]="toDateValue(minStartDate)"
                [max]="toDateValue(maxStartDate)"
                (dateChange)="onDatepickerMonthChange($event)"
              ></nb-datepicker>
            }
          </div>
          <!-- INITIATIVE TYPE -->
          <div class="form-field" [nbSpinner]="(initiativeTypes$ | async)?.loading">
            <label for="initiative_type">{{ 'Fidji ACT short label' | translate }}</label>
            @if (form.get('initiative_type')?.disabled) {
              <div id="initiative_type" (click)="showLockedInitiativeDialog()">
                {{ getInitiativeTypeDisplay() }}
              </div>
            } @else {
              <nb-select
                id="initiative_type"
                formControlName="initiative_type"
                fullWidth
              >
                @for (section of (initiativeTypes$ | async)?.data; track section) {
                  <nb-option-group [title]="section.name">
                    @for (it of section.children; track it) {
                      <nb-option [value]="it.id">
                        {{ it.friendly_name || it.setup_reference }}
                      </nb-option>
                    }
                  </nb-option-group>
                }
              </nb-select>
            }
          </div>
          <!-- PERIMETER -->
          <div class="form-field uneditable-field {{ readOnly ? 'disabled' : null }}">
            <label for="perimeter">{{ 'Perimeter' | translate }}</label>
            <div id="perimeter">{{ site?.name }} > {{ initiativePerimeter?.name }}</div>
          </div>
          <!-- CONFIDENCE ON FEASIBILITY -->
          <div class="form-field" [nbSpinner]="(confidencesOnFeasibility$ | async)?.loading">
            <label for="confidence_on_feasibility">{{ 'Confidence on feasibility' | translate }}</label>
            @if (form.get('confidence_on_feasibility')?.disabled) {
              <div id="confidence_on_feasibility" (click)="showLockedInitiativeDialog()">
                {{ getConfidenceOnFeasibilityDisplay() }}
              </div>
            } @else {
              <nb-select
                id="confidence_on_feasibility"
                formControlName="confidence_on_feasibility"
                fullWidth
              >
                @for (cof of (confidencesOnFeasibility$ | async)?.data; track cof) {
                  <nb-option [value]="cof.id">
                    {{ cof.name }}
                  </nb-option>
                }
              </nb-select>
            }
          </div>

          <!-- OPTIONAL FIELDS -->

          <!-- BUDGET -->
          <div class="form-field display-with-link"
               [class.display-with-link]="!isLocked && !readOnly"
               (click)="!readOnly && openBudgetForm()"
               [style.cursor]="(isLocked || readOnly) ? 'not-allowed' : 'pointer'"
               #firstOptionalField>
            <label for="budget">{{ "Budget" | translate }}</label>
            <div>
              <span id="budget">{{ getInitiativeBudget() ? (getInitiativeBudget() | ngxCurrency:currency) : '-' }}</span>
              @if (!isLocked && !readOnly) {
                <span class="right-chevron">
                  <i class="far fa-chevron-right fal"></i>
                </span>
              }
            </div>
          </div>
          <!-- KWH GAINS -->
          @if(selectedInitiativeType$ | async; as selectedInitiativeType) {
            @if(selectedInitiativeType.gain_type !== 'None') {
              <div class="form-field display-with-link"
                   [class.display-with-link]="!isLocked && !readOnly"
                   (click)="!readOnly && openGainsForm()"
                   [style.cursor]="(isLocked || readOnly) ? 'not-allowed' : 'pointer'"
                  >
                <label>{{ gainLabel }}</label>
                <div>
                  <span>{{ formatedGains }}</span>
                  @if (!isLocked && !readOnly) {
                    <span class="right-chevron">
                      <i class="far fa-chevron-right fal"></i>
                    </span>
                  }
                </div>
              </div>
            }
          }
          <!-- RESPONSIBLE -->
          <div class="form-field" [nbSpinner]="(users$ | async)?.loading">
            <label for="responsible">{{ 'Responsible' | translate }}</label>
            @if (form.get('responsible')?.disabled) {
              <div id="responsible" (click)="showLockedInitiativeDialog()">
                {{ displayValue || form.get('responsible')?.value || '-' }}
              </div>
            } @else {
              <input
                nbInput
                id="responsible"
                type="text"
                [value]="displayValue"
                (input)="onResponsibleInput($event)"
                fullWidth
                [nbAutocomplete]="autoResponsible"
                [disabled]="readOnly"
                placeholder="-"
                (blur)="autoSave('responsible')"
              >
              <nb-autocomplete #autoResponsible (selectedChange)="onResponsibleSelected($event)">
                @for (user of filteredUsers$ | async; track user) {
                  <nb-option [value]="user">
                    {{ user | ngxUsername }}
                  </nb-option>
                }
              </nb-autocomplete>
            }
          </div>
          <!-- INITIALLY PROPOSED BY -->
          <div class="form-field">
            <label for="initially_proposed_by">{{ "Action initially proposed by" | translate }}</label>
            @if (form.get('initially_proposed_by')?.disabled) {
              <div id="initially_proposed_by" (click)="showLockedInitiativeDialog()">
                {{ form.get('initially_proposed_by')?.value || '-' }}
              </div>
            } @else {
              <input
                nbInput
                id="initially_proposed_by"
                type="text"
                formControlName="initially_proposed_by"
                fullWidth
                placeholder="-"
                (blur)="autoSave('initially_proposed_by')"
              >
            }
          </div>
          <!-- Tags -->
          <div class="form-field" [nbSpinner]="(tags$ | async)?.loading">
            <label for="tags">{{ 'Action tags' | translate }}</label>
            @if (form.get('tags')?.disabled) {
              <div id="tags" (click)="showLockedInitiativeDialog()">{{ getTagsDisplay() }}</div>
            } @else {
              <nb-select
                id="tags"
                formControlName="tags"
                fullWidth
                multiple="true"
                placeholder="-"
              >
                @for (tag of (tags$ | async)?.data; track tag) {
                  <nb-option [value]="tag.id">
                    {{ tag.name }}
                  </nb-option>
                }
              </nb-select>
            }
          </div>
          <!-- CODE -->
          <div class="form-field">
            <label for="code">{{ "Action code" | translate }}</label>
            @if (form.get('code')?.disabled) {
              <div id="code" (click)="showLockedInitiativeDialog()">{{ form.get('code')?.value || '-' }}</div>
            } @else {
              <input
                nbInput
                id="code"
                type="text"
                formControlName="code"
                fullWidth
                placeholder="-"
              >
            }
          </div>
          <!-- DESCRIPTION -->
          <div class="form-field">
            <label for="description">{{ "Description of action" | translate }}</label>
            @if (form.get('description')?.disabled) {
              <div id="description" (click)="showLockedInitiativeDialog()">{{ form.get('description')?.value || '-' }}</div>
            } @else {
              <textarea
                nbInput
                id="description"
                type="text"
                formControlName="description"
                fullWidth
                placeholder="-"
                rows="30"
                (blur)="autoSave('description')"
              ></textarea>
            }
          </div>
        </div>

        <!-- DOCUMENTS TAB -->
        @if (activeFormTab === 'documents' && initiative && !addMode) {
          <app-entity-documents-tab
            [investIds]="[]"
            [assetIds]="[]"
            [actionIds]="initiative.id ? [initiative.id] : []"
            [perimeterIds]="initiative.site?.id ? [initiative.site.id] : (site?.id ? [site.id] : [])"
            [entityLabel]="initiative.label"
            (documentsChanged)="loadDocumentCount()"
          ></app-entity-documents-tab>
        }
      </nb-card-body>

      @if (addMode && anyFieldValueChanged() && !this.readOnly) {
        <nb-card-footer class="initiative wizard">
          <button nbButton type="submit" class="wizard-next" [disabled]="!isFormValid() || loading">
            {{ "Create action" | translate }}
          </button>
        </nb-card-footer>
      }
    </nb-card>
  </form>
}
